import { useQuery } from '@tanstack/react-query';
import { Header } from '@/components/layout/Header';
import { PageBody } from '@/components/layout/PageBody';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getCrossProjectIntelligence } from '@/lib/api';
import { CATEGORY_COLORS, formatTimeAgo } from '@/lib/pattern-categories';
import { getProjectDisplayName } from '@/lib/repo-label';
import type { ProjectIntelligence, GBrainPattern } from '@/lib/api';

function PatternCard({ pattern }: { pattern: GBrainPattern }): React.ReactElement {
  const colorClass = CATEGORY_COLORS[pattern.category] ?? 'bg-surface-elevated text-text-secondary';
  return (
    <div className="flex items-center gap-2 text-xs py-1">
      <Badge className={colorClass}>{pattern.category.replace(/_/g, ' ')}</Badge>
      <span className="text-text-tertiary">{formatTimeAgo(pattern.timestamp)}</span>
    </div>
  );
}

function ProjectCard({ project }: { project: ProjectIntelligence }): React.ReactElement {
  return (
    <Card>
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="truncate">{getProjectDisplayName({ name: project.name })}</span>
          {project.lifecyclePhase && (
            <Badge variant="secondary" className="text-[10px]">
              Phase {String(project.lifecyclePhase.current)}: {project.lifecyclePhase.name}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-2">
        <div className="text-xs text-text-secondary">{project.patternCount} patterns recorded</div>
        {project.recentPatterns.length > 0 && (
          <div className="space-y-0.5">
            {project.recentPatterns.map((p, i) => (
              <PatternCard key={`${p.timestamp}-${String(i)}`} pattern={p} />
            ))}
          </div>
        )}
        {project.patternCount === 0 && (
          <div className="text-xs text-text-tertiary">No intelligence data yet</div>
        )}
      </CardContent>
    </Card>
  );
}

export function IntelligencePage(): React.ReactElement {
  const { data, isLoading } = useQuery({
    queryKey: ['intelligence'],
    queryFn: getCrossProjectIntelligence,
    staleTime: 30_000,
  });

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <Header title="Intelligence" />
      <PageBody contentClassName="space-y-6">
        {isLoading && (
          <div className="text-sm text-muted-foreground">Loading intelligence data...</div>
        )}

        {data && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-text-primary">{data.projects.length}</div>
                  <div className="text-xs text-muted-foreground">Projects</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-text-primary">{data.totalPatterns}</div>
                  <div className="text-xs text-muted-foreground">Total Patterns</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-text-primary">
                    {Object.keys(data.categoryBreakdown).length}
                  </div>
                  <div className="text-xs text-muted-foreground">Pattern Categories</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-text-primary">
                    {data.projects.filter(p => p.lifecyclePhase !== null).length}
                  </div>
                  <div className="text-xs text-muted-foreground">Active Lifecycles</div>
                </CardContent>
              </Card>
            </div>

            {/* Category breakdown */}
            {Object.keys(data.categoryBreakdown).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Pattern Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(data.categoryBreakdown)
                      .sort(([, a], [, b]) => b - a)
                      .map(([category, count]) => (
                        <div key={category} className="flex items-center gap-2">
                          <Badge className={CATEGORY_COLORS[category] ?? 'bg-surface-elevated'}>
                            {category.replace(/_/g, ' ')}
                          </Badge>
                          <span className="text-sm font-medium text-text-primary">{count}</span>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Per-project intelligence */}
            <div>
              <h2 className="text-sm font-medium text-text-secondary mb-3">Project Intelligence</h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {data.projects
                  .sort((a, b) => b.patternCount - a.patternCount)
                  .map(project => (
                    <ProjectCard key={project.codebaseId} project={project} />
                  ))}
              </div>
            </div>
          </>
        )}
      </PageBody>
    </div>
  );
}
